// The pages of the assessment, as data.
//
// The meeting and the takeaway render the same page from the same model, at
// the same fixed slide size, which is what lets a drawing made in the room
// print exactly where it was drawn (Jimmy, 2026-09-29: "as if the drawing is
// on the original page, seamlessly"). A drawing is tied to its page by a
// digest of what the page shows. When the page still shows the same thing,
// the ink goes on it; when a figure has changed since, the ink stays with the
// earlier version rather than landing on numbers it was not drawn over.

import { goalsLocked } from '../brief/briefLocks.js';

// Goals and the illustrations are what an advisor presents; everything after
// them is follow-up reading, in the takeaway for the client to go through
// later and there in the meeting if the conversation turns to it (Amanda Cook,
// 2026-10-02). The illustrations are blank whiteboards, as many as the advisor
// starts (Jimmy, 2026-10-03).
export const STEP_ORDER = ['goals', 'whiteboard', 'tax', 'cash', 'investments', 'costs', 'protection', 'next'];
export const MEETING_STEPS = ['goals', 'whiteboard'];
export const isReading = step => STEP_ORDER.includes(step) && !MEETING_STEPS.includes(step);

// The first slide-based drawings. Anything older is printed the old way.
export const SLIDE_LAYOUT = 5;

export function fingerprint(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i += 1) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/**
 * Every page the meeting can show, keyed by step. A page with nothing to show
 * for this household is null: the meeting greys it out and the takeaway
 * leaves it out.
 */
export function buildPages({ facts, view, analysis }) {
  const ready = analysis?.availability?.state === 'ready';
  return {
    // The meeting opens on the goal sheet, with what the household said it
    // wants set into it. The heading is the one its stage of life suggests
    // unless the advisor wrote their own; only what prints is on the page, so
    // the reason behind a suggestion does not count as a change to it.
    goals: { name: facts.name || '', told: view.meeting.goals || facts.told || [], toldFromMeeting: Boolean(view.meeting.goals), toldShown: view.meeting.goalsShown !== false || goalsLocked(), toldLocked: goalsLocked(), lead: view.goalLead.text, sheet: view.goalSheet || null },
    // A blank sheet, for the advisor to draw on. Every whiteboard is this same
    // page, told apart by its board key, and it shows no figures, so nothing
    // drawn on one is ever set aside by a correction.
    whiteboard: { blank: true },
    // Only what the cash slide shows, so a change of reading year on the
    // Goals slide does not count as a change to this page.
    cash: view.goalSheet ? { timeline: view.cashTimeline, callouts: view.callouts.cash } : null,
    // Only a triangle turned off is recorded on the page, so a drawing made
    // over the triangle before the setting existed still lands on it.
    tax: { snapshot: view.taxPools, callouts: view.callouts.tax, ...(view.meeting?.triangle === false ? { triangle: false } : {}) },
    investments: ready
      ? {
        analysis: {
          exposure: analysis.exposure || null,
          style: analysis.style || null,
          scope: analysis.scope || null,
          simulated: Boolean(analysis.simulated),
        },
        allocation: view.allocation,
        callouts: view.callouts.investments,
      }
      : null,
    // Costs read the same statements as Investments, so they are shown on the
    // same terms: only while that analysis is ready. A replacement that is
    // still reading, or failed, withholds both rather than showing one slide
    // from the old statements beside another that says there is nothing.
    costs: ready && view.fees
      ? { fees: view.fees, unpriced: view.unpriced, advisory: view.advisory, allIn: view.allIn, callouts: view.callouts.costs }
      : null,
    protection: { checklist: view.protection },
    next: { workingOn: view.workingOn, questions: view.planQuestions },
  };
}

/**
 * What a page actually shows: the model with every finding the advisor left
 * out removed. This is what prints, and what a drawing is tied to.
 */
export function visiblePage(page) {
  if (!page) return null;
  if (!Array.isArray(page.callouts)) return page;
  return {
    ...page,
    callouts: page.callouts
      .filter(c => !c.hidden)
      .map(({ key, tone, headline, detail }) => ({ key, tone, headline, detail })),
  };
}

export function pageDigest(page) {
  return page ? fingerprint(JSON.stringify(visiblePage(page))) : '';
}

/** The drawing basis for a page in the slide layout, and for one whiteboard of several. */
export function slideBasis(step, page, board = null) {
  return { v: 1, step, board: board || null, layoutVersion: SLIDE_LAYOUT, page: pageDigest(page) };
}
